import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import GeneralApi from 'clients/api/GeneralApi'
import LastViewedPatient from 'model/LastViewedPatient'
import { userRolesBase } from 'model/User'
import UserSession from 'model/UserSession'
import { AppThunk } from 'store'
import { fetchPermissionsForUser } from 'user/user-slice'

interface UserState {
  userSession: UserSession
  lastViewedPatients: LastViewedPatient[]
}

const initialState: UserState = {
  userSession: {} as UserSession,
  lastViewedPatients: [],
}

const userSessionSlice = createSlice({
  name: 'userSession',
  initialState,
  reducers: {
    fetchUserSession(state, { payload }: PayloadAction<UserSession>) {
      fetchPermissionsForUser(payload.role as userRolesBase)
      state.userSession = payload
    },
    fetchLastViewedPatientsSuccess: (state, { payload }: PayloadAction<LastViewedPatient[]>) => {
      state.lastViewedPatients = payload
    },
    savePatientToLastViewed: (state, { payload }: PayloadAction<LastViewedPatient>) => {
      const filteredPatients = state.lastViewedPatients.filter(item => item.id !== payload.id)
      state.lastViewedPatients = [payload, ...filteredPatients].slice(0, 3)
      GeneralApi.saveLastViewedPatients(state.lastViewedPatients)
    },
  },
})

export const {
  fetchUserSession,
  savePatientToLastViewed,
  fetchLastViewedPatientsSuccess,
} = userSessionSlice.actions

export const saveUserSession = (userSession: UserSession): AppThunk => async dispatch => {
  dispatch(fetchUserSession(userSession))
  dispatch(fetchPermissionsForUser(userSession.role as userRolesBase, userSession.admin))
  window.sessionStorage.setItem('userSession', JSON.stringify(userSession))
}

export const clearUserSession = (): AppThunk => async dispatch => {
  dispatch(fetchUserSession({} as UserSession))
  dispatch(fetchPermissionsForUser({} as userRolesBase))
  window.sessionStorage.setItem('userSession', JSON.stringify({}))
}

export const loadLastViewedPatients = (): AppThunk => async dispatch => {
  const result = await GeneralApi.getLastViewedPatients()
  if (result) {
    dispatch(fetchLastViewedPatientsSuccess(result.data.lastViewedPatients))
  }
}

export const addPatientToLastViewed = (
  id: string,
  fullName?: string,
  autoSaved?: boolean,
): AppThunk => async dispatch => {
  if (!id) {
    return
  }
  const lastViewdPatient = { id, fullName, autoSaved }
  dispatch(savePatientToLastViewed(lastViewdPatient))
}

export default userSessionSlice.reducer
